import { Box, MenuItem, Stack, TextField } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";

import {
  CustomTextEditorFields,
  CustomTextModeToggle,
} from "./CustomTextEditorFields";
import { writeCollapsedSiblings, writeSibling } from "./siblingWrites";
import { useCustomTextEditor } from "./useCustomTextEditor";
import { usePopupEditorKeyboard } from "./usePopupEditorKeyboard";

import type { ICellEditorParams } from "ag-grid-community";
import type { JsonValue } from "../utils/popupCustomText";

export interface GroupedEnumFieldOption {
  value: string;
  label: string;
}

export interface GroupedEnumField {
  /** The row's own benefitKey. */
  key: string;
  /** Field label shown above its dropdown (e.g. "Exam"). */
  label: string;
  initialValue: string | undefined;
}

export interface GroupedEnumCellEditorParams {
  /** First entry's `key` must be the parent row's own benefitKey — its value
   * is written via AG Grid's standard commit path; the rest are siblings
   * written directly via `node.setDataValue` lookups by benefitKey. */
  fields: GroupedEnumField[];
  /** Shared option list rendered in every field's dropdown. */
  options: GroupedEnumFieldOption[];
  initialCustomText?: string | null;
  onCustomTextChange?: (
    customText: string | null,
    originalValue?: JsonValue,
  ) => void;
}

type Props = ICellEditorParams & GroupedEnumCellEditorParams;

/**
 * AG Grid popup cell editor for a group of independent enum fields collapsed
 * into a single displayed row (e.g. Vision's Frequency - Exam/Lenses/Frames).
 * Unlike PreExistingConditionCellEditor/GuaranteeIssueCellEditor, no field
 * gates another — every dropdown is always visible and shares one option
 * list. On save, each field's value is written to its own benefitKey row.
 */
export const GroupedEnumCellEditor = forwardRef<unknown, Props>(
  function GroupedEnumCellEditor(props, ref) {
    const {
      fields,
      options,
      initialCustomText,
      onCustomTextChange,
      api,
      colDef,
      column,
      node,
      stopEditing,
    } = props;

    const [drafts, setDrafts] = useState<Record<string, string>>(() =>
      Object.fromEntries(fields.map((f) => [f.key, f.initialValue ?? ""])),
    );
    const customText = useCustomTextEditor(
      initialCustomText,
      onCustomTextChange,
      stopEditing,
    );
    const { editingCustomText } = customText;

    const parentKey = fields[0]?.key;
    const lastValueRef = useRef<unknown>(fields[0]?.initialValue ?? null);

    useImperativeHandle(ref, () => ({
      getValue: () => lastValueRef.current,
      isPopup: () => true,
      isCancelBeforeStart: () => false,
      isCancelAfterEnd: () => false,
    }));

    const handleSave = (): void => {
      if (editingCustomText) {
        lastValueRef.current = fields[0]?.initialValue ?? null;
        customText.saveCustomText(fields[0]?.initialValue ?? null);
        return;
      }

      const columnField = colDef?.field;
      if (initialCustomText) onCustomTextChange?.(null);

      lastValueRef.current = drafts[parentKey] || null;

      if (columnField && node?.data) {
        writeCollapsedSiblings(
          node as { data: Record<string, unknown> },
          columnField,
          fields
            .filter((f) => f.key !== parentKey)
            .map((f) => [f.key, drafts[f.key] || null]),
        );
      }

      stopEditing(true);

      setTimeout(() => {
        for (const field of fields) {
          if (field.key === parentKey) continue;
          writeSibling(
            { api, column, colDef },
            field.key,
            drafts[field.key] || null,
            { silent: true },
          );
        }
        writeSibling(
          { api, column, colDef },
          parentKey,
          drafts[parentKey] || null,
        );
        if (node) api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    };

    const handleCancel = (): void => {
      lastValueRef.current = fields[0]?.initialValue ?? null;
      stopEditing(true);
    };

    const containerRef = useRef<HTMLDivElement | null>(null);
    usePopupEditorKeyboard(containerRef, handleSave, handleCancel);

    return (
      <Box
        ref={containerRef}
        sx={{
          p: 2,
          width: 440,
          backgroundColor: "background.paper",
          boxShadow: 3,
          borderRadius: 1,
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="subtitle1">
            <strong>Edit Benefit Value</strong>
          </Typography>

          {customText.customTextSupported && (
            <CustomTextModeToggle
              editMode={customText.editMode}
              onChange={customText.setEditMode}
            />
          )}

          {!editingCustomText &&
            fields.map((field) => (
              <TextField
                key={field.key}
                select
                size="small"
                label={field.label}
                value={drafts[field.key] ?? ""}
                onChange={(e) =>
                  setDrafts((prev) => ({
                    ...prev,
                    [field.key]: e.target.value,
                  }))
                }
                fullWidth
              >
                <MenuItem value="">
                  <em>(none)</em>
                </MenuItem>
                {options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            ))}

          {editingCustomText && (
            <CustomTextEditorFields
              content={customText.draftCustomText}
              onUpdate={customText.setDraftCustomText}
              onCharacterCount={customText.setDraftCharacterCount}
            />
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  },
);
